import type { SVGProps } from 'react';

/**
 * Marque Taki : le logo badge et l'icone.
 *
 * Sources livrees par le fondateur, rangees a cote :
 *   - `src/assets/taki-logo-full.svg` -> `TakiLogo`
 *   - `src/assets/taki-icon.svg`      -> `TakiIcon`
 *
 * Le LOGO garde ses couleurs de marque EN DUR (lime #cbfb45 + encre #252626) :
 * c'est une identite, elle ne se repeint pas avec le branding d'un client et ne
 * suit pas le theme. C'est la seule exception assumee a la regle "aucun hex en
 * dur" ; la regle d'or (le lime est rare, jamais en aplat de fond) vise les
 * surfaces de l'UI, pas le logo, qui EST le signal.
 *
 * L'ICONE, elle, est monochrome et rendue en `currentColor` : on la colore via
 * une classe de texte (ex `text-ink-900`), donc elle suit le theme.
 */

/** Couleurs de marque, figees (voir le commentaire ci-dessus). */
const BRAND_LIME = '#cbfb45';
const BRAND_INK = '#252626';

/**
 * Logo Taki complet : badge lime, mot "taki" et le chevron.
 * Ratio ~2.95:1. On fixe la hauteur, la largeur suit (ex `className="h-8"`).
 *
 * @example <TakiLogo className="h-8" />
 */
export function TakiLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 1463.15 496.61"
      role="img"
      aria-label="Taki"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect fill={BRAND_LIME} x="0" y=".1" width="1463.14" height="496.42" rx="227.04" ry="227.04" />
      <g fill={BRAND_INK}>
        <path d="M240.04,328.22l.02-102.51h-26.6s.01-56.54.01-56.54h26.6s0-43.42,0-43.42l71.41-12.51v55.95s41.66,0,41.66,0v56.54s-41.68,0-41.68,0l-.02,109.94h41.67s-.01,56.54-.01,56.54h-38.54c-49.3-.02-74.53-13.52-74.52-63.99Z" />
        <path d="M500.99,392.24c-2.54-13.7-4.49-27.58-5.66-41.47-7.44,28.17-27.98,45.58-59.48,45.57-40.5,0-59.27-22.51-59.26-67.7v-44.21s117.2-30.69,117.2-30.69v-27.97s-45.77,0-45.77,0v24.26s-71.41-.01-71.41-.01v-6.45c.01-55.17,31.12-78.25,94.31-78.23,64.56.01,94.29,26.04,94.28,81.2v61.82c-.02,28.17,1.54,56.54,6.82,83.92h-71.01ZM494.35,335.7c-.39-8.8-.58-17.61-.58-26.41v-15.45s-45.78,13.29-45.78,13.29v28.56s46.36,0,46.36,0Z" />
        <path d="M597.49,100.38h71.4s-.06,291.88-.06,291.88h-71.4s.06-291.88.06-291.88ZM671.79,275.28l47.16-106.02h73.56s-49.51,106.03-49.51,106.03l49.47,116.99h-74.14s-46.54-117.01-46.54-117.01Z" />
        <polygon points="1128.53 276.04 1175.88 169.61 1249.71 169.63 1200.01 276.05 1249.67 393.49 1175.24 393.48 1128.53 276.04" />
        <polygon points="1124.42 217.73 1077.08 324.15 1003.24 324.14 1052.94 217.72 1003.28 100.28 1077.71 100.29 1124.42 217.73" />
        <path d="M811.5,100.62h71.4s0,48.53,0,48.53h-71.4s0-48.53,0-48.53ZM811.48,169.28h71.4s-.04,223.03-.04,223.03h-71.4s.04-223.03.04-223.03Z" />
      </g>
    </svg>
  );
}

/**
 * Icone Taki (le chevron seul), monochrome et thematisable via `currentColor`.
 * Ratio ~0.84:1 (plus haute que large).
 *
 * @example <TakiIcon className="h-6 text-ink-900" />
 */
export function TakiIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 96.29 114.55"
      fill="currentColor"
      role="img"
      aria-label="Taki"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <polygon points="48.95 68.66 67.44 27.09 96.29 27.09 76.87 68.67 96.27 114.55 67.2 114.54 48.95 68.66" />
      <polygon points="47.34 45.88 28.85 87.46 0 87.46 19.42 45.88 .02 0 29.09 0 47.34 45.88" />
    </svg>
  );
}
